import { L10n, setCulture } from '@syncfusion/ej2-base';
import { generateSummary, Timezone } from '@syncfusion/ej2-schedule';
import {
  NewScheduleCreationParams,
  EventData,
  ScheduleUpdateParams,
  ExceptionData,
  ExceptionCreationData,
  isSingleDayEventType,
} from 'utils/types/learningSchedule';
import { PopupOpenEventArgs } from '@syncfusion/ej2-react-schedule';
import { getEventTypesOptions } from 'Pages/LearningSchedule/constants/constants';
import { getCurrentLocale } from 'utils/localeHelper';

const currentLocale = getCurrentLocale() as keyof typeof localeMap;
export const localeMap = {
  en: 'en-US',
  fr: 'fr-CH',
};

export function customGenerateSummary(
  recurrencePattern: string,
  localeStrings: Record<string, string>,
  dayNamesShort: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
): string {
  if (!recurrencePattern) return localeStrings['none'] || 'None';

  const parts = recurrencePattern.split(';').reduce((acc, part) => {
    const [key, val] = part.split('=');
    acc[key] = val;
    return acc;
  }, {} as Record<string, string>);
  const freq = parts.FREQ?.toLowerCase();
  const interval = parts.INTERVAL;
  const byDay = parts.BYDAY ? parts.BYDAY.split(',') : [];
  // const bySetPos = parts.BYSETPOS;
  const freqLabel = localeStrings[freq] || freq;
  const everyLabel = localeStrings['repeatEvery'] || 'Repeat every';

  let summary = `${everyLabel} ${interval} ${freqLabel}`;

  if (byDay.length) {
    const localizedDays = byDay
      .map((day) => {
        const index = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'].indexOf(day);
        return index !== -1 ? dayNamesShort[index] : day;
      })
      .join(', ');
    summary += ` ${localeStrings['on'] || 'on'} ${localizedDays}`;
  }

  return summary;
}

export const generateReadableRecurrancePattern = (
  recurrancePattern?: string
): string => {
  setCulture(localeMap[currentLocale] || 'en-US');
  if (!recurrancePattern) {
    return '';
  }
  const l10n = new L10n('recurrenceeditor', {
    none: 'None',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    month: 'Month',
    yearly: 'Yearly',
    never: 'Never',
    until: 'Until',
    count: 'Count',
    first: 'First',
    second: 'Second',
    third: 'Third',
    fourth: 'Fourth',
    last: 'Last',
    repeat: 'Repeat',
    repeatEvery: 'Repeat every',
    on: 'Repeat On',
    end: 'End',
    onDay: 'Day',
    days: 'Day(s)',
    weeks: 'Week(s)',
    months: 'Month(s)',
    years: 'Year(s)',
    every: 'every',
    summaryTimes: 'time(s)',
    summaryOn: 'on',
    summaryUntil: 'until',
    summaryRepeat: 'Repeats',
    summaryDay: 'day(s)',
    summaryWeek: 'week(s)',
    summaryMonth: 'month(s)',
    summaryYear: 'year(s)',
    monthWeek: 'Month Week',
    monthPosition: 'Month Position',
    monthExpander: 'Month Expander',
    yearExpander: 'Year Expander',
    repeatInterval: 'Repeat Interval',
  });
  const frL10n = new L10n('recurrenceeditor', {
    none: 'Aucun',
    daily: 'Quotidien',
    weekly: 'Hebdomadaire',
    monthly: 'Mensuel',
    month: 'Mois',
    yearly: 'Annuel',
    never: 'Jamais',
    until: "Jusqu'à",
    count: 'Nombre',
    first: 'Premier',
    second: 'Deuxième',
    third: 'Troisième',
    fourth: 'Quatrième',
    last: 'Dernier',
    repeat: 'Répéter',
    repeatEvery: 'Répéter tous les',
    on: 'Répéter le',
    end: 'Fin',
    onDay: 'Jour',
    days: 'Jour(s)',
    weeks: 'Semaine(s)',
    months: 'Mois(s)',
    years: 'Année(s)',
    every: 'chaque',
    summaryTimes: 'fois',
    summaryOn: 'le',
    summaryUntil: "jusqu'à",
    summaryRepeat: 'Répète',
    summaryDay: 'jour(s)',
    summaryWeek: 'semaine(s)',
    summaryMonth: 'mois',
    summaryYear: 'an(s)',
    monthWeek: 'Semaine du mois',
    monthPosition: 'Position du mois',
    monthExpander: 'Développeur de mois',
    yearExpander: "Développeur d'année",
    repeatInterval: 'Intervalle de répétition',
  });
  if (currentLocale === 'fr') {
    return generateSummary(recurrancePattern, frL10n, localeMap[currentLocale]);
  } else {
    return generateSummary(recurrancePattern, l10n, currentLocale);
  }
};

export const serializeCreateEvent = (
  data: EventData
): NewScheduleCreationParams => {
  return {
    learningEvent: {
      learningEventTypeId: data.EventType,
      title: data.Subject,
      ...(data.Description != null &&
        data.Description !== '' && { description: data.Description }),
    },
    schedule: {
      scheduleFacilitatorIds: data.FacilitatorId || [],
      scheduleProjectIds: data.ProjectId,
      startDateTime: data.StartTime,
      endDateTime: data.EndTime,
      isAllDay: data.IsAllDay,
      ...(data.CreatorTimeZone && {
        CreatorTimeZone: data.CreatorTimeZone,
      }),
      isOldEvent: data.isOldEvent,
      recurrencePattern: {
        recurrenceRule: data.RecurrenceRule || '',
        recurrenceRuleFriendlyName: data.RecurrenceRuleFriendlyName || '',
      },
    },
  };
};

export const serializeUpdateEvent = (
  data: EventData,
  shouldResetExceptions = false
): ScheduleUpdateParams => {
  return {
    scheduleId: data.Id,
    learningEvent: {
      learningEventTypeId: data.EventType,
      title: data.Subject,
      ...(data.Description != null &&
        data.Description !== '' && { description: data.Description }),
    },
    schedule: {
      scheduleFacilitatorIds: data.FacilitatorId,
      scheduleProjectIds: data.ProjectId,
      startDateTime: data.StartTime,
      endDateTime: data.EndTime,
      isAllDay: data.IsAllDay,
      ...(data.CreatorTimeZone && {
        CreatorTimeZone: data.CreatorTimeZone,
      }),
      isOldEvent: data.isOldEvent,
      recurrencePattern: {
        recurrenceRule: data.RecurrenceRule || '',
        recurrenceRuleFriendlyName: data.RecurrenceRuleFriendlyName || '',
        oldRecurrenceRuleFriendlyName: data.OldRecurrenceRuleFriendlyName || '',
      },
      recurrenceException: data.RecurrenceException || '',
    },
    resetExceptions: shouldResetExceptions,
  };
};

export const getEventTypeIdFromCode = (code: string) => {
  const EventTypesOptions = getEventTypesOptions();
  const eventType = EventTypesOptions.find((option) => option.code === code);
  return eventType?.value || '';
};

export const attachListenersToSchedule = (
  args: PopupOpenEventArgs,
  onUpdateEventField: (key: string, value: any) => void
) => {
  const fieldsToListen = [
    {
      key: 'startTime',
      element: '#StartTime',
      value: 'value',
    },
    {
      key: 'endTime',
      element: '#EndTime',
      value: 'value',
    },
    {
      key: 'isAllDay',
      element: '#IsAllDay',
      value: 'checked',
    },
    {
      key: 'recurrenceRule',
      element: '.e-recurrenceeditor',
      value: 'value',
    },
  ];

  for (const field of fieldsToListen) {
    const element = args.element.querySelector(field.element) as any;
    if (element && element.ej2_instances) {
      const originalChange = element.ej2_instances[0].change;
      element.ej2_instances[0].change = (e: any) => {
        let value = e[field.value];
        if (!value) {
          value = null;
        }
        onUpdateEventField(field.key, value);
        if (originalChange) originalChange.call(element.ej2_instances[0], e);
      };
    }
  }
};

export const serializeCreateException = (
  data: ExceptionData,
  parentId: string,
  recurrenceException: string
): ExceptionCreationData => {
  return {
    learningEvent: {
      learningEventTypeId: data.EventType,
      title: data.Subject,
      ...(data.Description != null &&
        data.Description !== '' && { description: data.Description }),
    },
    schedule: {
      scheduleFacilitatorIds: data.FacilitatorId,
      scheduleProjectIds: data.ProjectId,
      startDateTime: data.StartTime,
      endDateTime: data.EndTime,
      isAllDay: data.IsAllDay,
      parentId: parentId,
      isOldEvent: data.isOldEvent,
      ...(data.CreatorTimeZone && {
        CreatorTimeZone: data.CreatorTimeZone,
      }),
    },
    recurrenceException: recurrenceException,
  };
};

export const getEventTypeValueFromCode = (code: string) => {
  const EventTypesOptions = getEventTypesOptions();
  const eventType = EventTypesOptions.find((option) => option.code === code);
  return eventType?.value.toString() || '';
};

export const getEventData = ({
  projectIds,
  eventTitle,
  eventDescription,
  eventType,
  data,
  facilitators,
}: {
  projectIds: string[];
  eventTitle: string;
  eventDescription?: string | null;
  eventType: string;
  data: EventData;
  facilitators: string[];
}) => {
  return {
    StartTime: data.StartTime,
    EndTime: data.EndTime,
    RecurrenceRule: data.RecurrenceRule,
    RecurrenceRuleFriendlyName: data.RecurrenceRuleFriendlyName,
    OldRecurrenceRuleFriendlyName: data.OldRecurrenceRuleFriendlyName,
    Id: data.Id,
    ProjectId: projectIds,
    Subject: eventTitle,
    Description: eventDescription ?? data.Description ?? null,
    EventType: eventType,
    IsAllDay: data.IsAllDay,
    FacilitatorId: facilitators,
    RecurrenceException: data.RecurrenceException,
    ...(data.CreatorTimeZone && {
      CreatorTimeZone: data.CreatorTimeZone,
    }),
    isOldEvent: data.isOldEvent,
  };
};

export const isSingleDayAllDayEvent = (
  event: isSingleDayEventType
): boolean => {
  return (
    event.IsAllDay &&
    new Date(event.StartTime).toDateString() ===
      new Date(event.EndTime).toDateString()
  );
};

// Handle all-day events by extracting only the date part
export const processAllDayEvent = (
  date: Date | string,
  isEndDate: boolean = false
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Extract only the date part (YYYY-MM-DD) and set time to 00:00:00
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const result = `${year}-${month}-${day}T00:00:00.000Z`;
  return result;
};

export const processEndTime = (
  startTime: string,
  endTime: string,
  isAllDay: boolean,
  isSingleDay: boolean
): string => {
  if (!isAllDay) return endTime;

  if (isSingleDay) {
    // For single-day events, use the start date for both start and end
    return processAllDayEvent(startTime);
  } else {
    // For multi-day events, we need to handle the end date differently
    // Syncfusion returns exclusive end date, but for display we need inclusive end date
    const endDate = new Date(endTime);

    // For multi-day all-day events, we want the end date to be the last day the event should appear
    // Since Syncfusion gives us the next day, we subtract one day to get the actual end date
    const adjustedDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
    const result = processAllDayEvent(adjustedDate);
    return result;
  }
};

// Convert local time to UTC for API calls
export const convertLocalTimeToUTC = (localTime: string): string => {
  const timezone = new Timezone();
  const localDate = new Date(localTime);

  // Convert from user's local timezone to UTC
  const utcDate = timezone.convert(
    localDate,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    'UTC'
  );

  return utcDate.toISOString();
};

// Convert UTC time to local timezone for display
export const convertUTCToLocalTime = (utcTime: string): Date => {
  const timezone = new Timezone();
  const utcDate = new Date(utcTime);

  // Convert from UTC to user's local timezone
  const localDate = timezone.convert(
    utcDate,
    'UTC',
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  return localDate;
};
export const processEventTimesForEdit = (
  data: isSingleDayEventType,
  isSingleDayEvent: boolean,
  isRecurringEvent: boolean = false
): { startTime: string; endTime: string } => {
  if (isRecurringEvent && !data.IsAllDay) {
    return {
      startTime: new Date(data.StartTime).toISOString(),
      endTime: new Date(data.EndTime).toISOString(),
    };
  }
  if (data.IsAllDay || (data.IsAllDay && isRecurringEvent)) {
    return {
      startTime: processAllDayEvent(data.StartTime),
      endTime: processEndTime(
        data.StartTime,
        data.EndTime,
        data.IsAllDay,
        isSingleDayEvent
      ),
    };
  } else {
    return {
      startTime: convertLocalTimeToUTC(data.StartTime),
      endTime: convertLocalTimeToUTC(data.EndTime),
    };
  }
};

// Process event times for creation (handles both all-day and timed events)
export const processEventTimesForCreate = (
  data: isSingleDayEventType,
  isSingleDayEvent: boolean
): { startTime: string; endTime: string } => {
  if (data.IsAllDay) {
    return {
      startTime: processAllDayEvent(data.StartTime),
      endTime: processEndTime(
        data.StartTime,
        data.EndTime,
        data.IsAllDay,
        isSingleDayEvent
      ),
    };
  } else {
    // For timed events, keep original times (they should already be in correct format)
    return {
      startTime: data.StartTime,
      endTime: data.EndTime,
    };
  }
};

// Shared function to transform events for schedule components
export const transformEventForSchedule = (event: isSingleDayEventType) => {
  const isOldEvent = event.isOldEvent;

  if (isOldEvent) {
    // Simple transformation for old events
    const startTime = new Date(event.StartTime);
    const endTime = new Date(event.EndTime);

    // For all-day events, ensure times are normalized to midnight UTC
    if (event.IsAllDay) {
      const startDate = new Date(
        startTime.getUTCFullYear(),
        startTime.getUTCMonth(),
        startTime.getUTCDate()
      );
      const endDate = new Date(
        endTime.getUTCFullYear(),
        endTime.getUTCMonth(),
        endTime.getUTCDate()
      );

      return {
        ...event,
        StartTime: startDate,
        EndTime: endDate,
        IsAllDay: true,
      };
    }

    // For timed events, convert from UTC to user's local timezone for display
    if (!event.IsAllDay) {
      return {
        ...event,
        StartTime: convertUTCToLocalTime(event.StartTime),
        EndTime: convertUTCToLocalTime(event.EndTime),
        IsAllDay: false,
      };
    }
  } else {
    // Enhanced transformation for new events with timezone conversion

    if (event.IsAllDay) {
      const startParts = extractDateParts(event.StartTime);
      const endParts = extractDateParts(event.EndTime);

      // Check if it's a single-day event (start and end dates are the same)
      const isSingleDayEvent =
        event.StartTime.split('T')[0] === event.EndTime.split('T')[0];

      if (isSingleDayEvent) {
        // For single-day all-day events, create Date objects in local timezone
        const startDateObj = new Date(
          startParts.year,
          startParts.month - 1,
          startParts.day,
          0,
          0,
          0
        );
        const endDateObj = new Date(
          startParts.year,
          startParts.month - 1,
          startParts.day,
          0,
          0,
          0
        );

        return {
          ...event,
          StartTime: startDateObj,
          EndTime: endDateObj,
          IsAllDay: true,
        };
      } else {
        // For multi-day events, create Date objects in local timezone
        const startDateObj = new Date(
          startParts.year,
          startParts.month - 1,
          startParts.day,
          0,
          0,
          0
        );

        // For multi-day events, the end date should be the day after the last day
        // to make it exclusive for Syncfusion display
        const endDateObj = new Date(
          endParts.year,
          endParts.month - 1,
          endParts.day + 1, // Add one day to make it exclusive
          0,
          0,
          0
        );

        return {
          ...event,
          StartTime: startDateObj,
          EndTime: endDateObj,
          IsAllDay: true,
        };
      }
    }

    // For timed events, convert from UTC to user's local timezone for display
    if (!event.IsAllDay) {
      return {
        ...event,
        StartTime: convertUTCToLocalTime(event.StartTime),
        EndTime: convertUTCToLocalTime(event.EndTime),
        IsAllDay: false,
      };
    }
  }
};

export const extractDateParts = (dateString: string) => {
  // Handle format "2025-07-30T00:00:00+00:00"
  const datePart = dateString.split('T')[0]; // Get "2025-07-30"
  const [year, month, day] = datePart.split('-').map(Number);
  return { year, month, day };
};
